import { useEffect, useLayoutEffect, useRef } from 'react';
import { useAuthStore, useImpersonation } from '../stores/auth';
import { tokenManager } from '../lib/api';

interface ImpersonationBannerProps {
  /**
   * 실제 렌더 높이 보고 — Layout이 아래 배너·바·본문 오프셋을 이 값으로 쌓는다.
   *
   * ⚠️ 이 배너는 **언제나 최상단**(`top-0`)이라 `topOffset`을 받지 않는다. 형제 배너들과 다른
   * 점은 그것뿐이고, 높이 보고 관례는 같다(`VerificationPendingBanner`·`ActionItemsBar`).
   */
  onHeightChange?: (height: number) => void;
}

export default function ImpersonationBanner({ onHeightChange }: ImpersonationBannerProps = {}) {
  const impersonation = useImpersonation();
  const { endImpersonation } = useAuthStore();
  const ref = useRef<HTMLDivElement>(null);

  const shouldRender = !!impersonation;

  useEffect(() => {
    if (!shouldRender) onHeightChange?.(0);
  }, [shouldRender, onHeightChange]);

  useLayoutEffect(() => {
    if (!shouldRender || !ref.current) return;
    const element = ref.current;
    const update = () => onHeightChange?.(element.offsetHeight);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(element);
    return () => observer.disconnect();
  }, [shouldRender, onHeightChange]);

  if (!impersonation) {
    return null;
  }

  const handleEndImpersonation = () => {
    // 빙의 토큰만 삭제 (기존 로그인은 유지)
    tokenManager.clearImpersonationToken();
    endImpersonation();
    // 창 닫기 시도, 실패하면 로그인 페이지로 이동
    window.close();
    // window.close()가 동작하지 않는 경우 (브라우저 보안 정책)
    setTimeout(() => {
      window.location.href = '/login';
    }, 100);
  };

  return (
    <div
      ref={ref}
      className="fixed top-0 left-0 right-0 z-[100] bg-red-600 text-white px-4 py-2 text-sm"
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-lg">🔴</span>
          <span>
            관리자 <strong>{impersonation.adminEmail}</strong>님이{' '}
            <strong>{impersonation.targetUserName}</strong>으로 접속 중입니다
          </span>
        </div>
        <button
          onClick={handleEndImpersonation}
          className="px-3 py-1 bg-white text-red-600 rounded text-sm font-medium hover:bg-red-50 transition-colors"
        >
          빙의 종료
        </button>
      </div>
    </div>
  );
}
